import { currentSession, login, type Session, setUnauthorizedHandler } from "./trpc";

/**
 * Состояние сессии как external store — тем же приёмом, что и стор данных.
 *
 * Живёт ЗДЕСЬ, а не в `SyncStatus`, потому что 401 — понятие HTTP и куки, а `Transport`
 * намеренно абстракция без них. Затащив авторизацию в публичный тип пакета, мы обязали бы
 * `memoryDriver` и тесты стора её моделировать. Стор при 401 и так деградирует правильно:
 * ловит ошибку, ставит `online:false` и ничего не чистит.
 */

export type { Session };

export type SessionState =
	| "probing" // спрашиваем — решения ещё нет
	| "authenticated"
	| "anonymous" // сервер ответил: не вошёл
	| "unknown" // сервер не ответил вовсе (оффлайн)
	| "expired"; // сессия была, сервер вернул 401 на рабочем запросе

export type SessionStore = {
	subscribe: (listener: () => void) => () => void;
	getState: () => SessionState;
	probe: () => Promise<void>;
	signIn: (password: string) => Promise<SignInResult>;
	/** 401 с любого рабочего запроса. */
	onUnauthorized: () => void;
};

/** Три исхода, а не булево: «не подошёл пароль» и «нет связи» — разные сообщения. */
export type SignInResult = "ok" | "wrong-password" | "offline";

type Deps = {
	probe: () => Promise<Session | null>;
	login: (password: string) => Promise<boolean>;
};

/**
 * Фабрика — ради тестируемости: машина состояний проверяется без сети, DOM и tRPC.
 */
export function createSessionStore(deps: Deps): SessionStore {
	const listeners = new Set<() => void>();
	let state: SessionState = "probing";
	// Ответ более старого запроса не должен перетирать результат более нового: «вернулась
	// сеть» и нажатая «Повторить» идут подряд, и без счётчика первый откатил бы второй.
	let generation = 0;

	function set(next: SessionState): void {
		// 401 приходит пачками — по одному на каждый запрос батча и на каждый тик синка.
		if (next === state) {
			return;
		}
		state = next;
		for (const listener of listeners) {
			listener();
		}
	}

	return {
		subscribe: (listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},

		getState: () => state,

		probe: async () => {
			const mine = ++generation;
			const session = await deps.probe();
			if (mine !== generation) {
				return;
			}
			set(session === null ? "unknown" : session.authenticated ? "authenticated" : "anonymous");
		},

		signIn: async (password) => {
			try {
				if (!(await deps.login(password))) {
					return "wrong-password";
				}
				// Перевести состояние обязан сам `signIn`: гейт смотрит на него, и без этого
				// форма (особенно оверлей «сессия истекла») просто не исчезла бы.
				generation++;
				set("authenticated");
				return "ok";
			} catch {
				// Сеть отвалилась прямо во время входа. Состояние не трогаем: мы по-прежнему
				// не знаем, вошли мы или нет, — врать в любую сторону хуже, чем сказать
				// «нет связи».
				return "offline";
			}
		},

		onUnauthorized: () => {
			// Только из `authenticated`. `store.start()` идёт параллельно с `probe()`, и у
			// невошедшего посетителя стор получит 401 раньше, чем ответит `/auth/me`, —
			// иначе чистая форма входа подменялась бы оверлеем «сессия истекла».
			if (state === "authenticated") {
				// Поколение — чтобы уже летящий `/auth/me` не вернул нас в `authenticated`:
				// он спрашивал ДО отказа и ответит «вошли», хотя рабочие запросы уже отвергают.
				generation++;
				set("expired");
			}
		}
	};
}

const store = createSessionStore({ probe: currentSession, login });

export const subscribeSession = store.subscribe;
export const getSession = store.getState;
export const probeSession = store.probe;
export const signIn = store.signIn;

/**
 * Проводка к сети. Живёт здесь, а не в экране: `lib/` — та граница, где про сеть знать
 * можно, а `components/**` про неё не знает вовсе.
 */
export function initSessionWatch(): void {
	setUnauthorizedHandler(store.onUnauthorized);
	window.addEventListener("online", () => void store.probe());
}
